import React, { useState, useCallback } from 'react';
import './channel.css';
import Header from '../../Components/Header/Header';
import { toast } from 'react-toastify';
import { useAuthContext } from '../../hooks/UseAuthContext';
import api from '../../api';
import GetChannel from './GetChannel';

const NICHES = [
  'celebrity/politics',
  'history',
  'philosophy',
  'commentary',
];

function Channel() {
  const [createChannel, setCreateChannel]     = useState(false);
  const [niche, setNiche]                     = useState('');
  const [loading, setLoading]                 = useState(false);
  const [channelName, setChannelName]         = useState('');
  const [logo, setLogo]                       = useState(null);
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [backgroundVideo, setBackgroundVideo] = useState(null);
  const [channelRefreshKey, setChannelRefreshKey] = useState(Date.now());
  const [uploadProgress, setUploadProgress]   = useState({ logo: 0, image: 0, video: 0 });
  const [editMode, setEditMode]               = useState(false);
  const [editingChannel, setEditingChannel]   = useState(null);

  const { user } = useAuthContext();

  // ── Form reset ─────────────────────────────────────────────
  const resetForm = useCallback(() => {
    setChannelName('');
    setNiche('');
    setLogo(null);
    setBackgroundImage(null);
    setBackgroundVideo(null);
    setUploadProgress({ logo: 0, image: 0, video: 0 });
    setEditMode(false);
    setEditingChannel(null);
  }, []);

  // ── Submit (create or update) ──────────────────────────────
  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData();
    formData.append('channelName', channelName);
    formData.append('niche', niche);
    if (logo) formData.append('logo', logo);
    if (niche === 'commentary') {
      if (backgroundImage) formData.append('backgroundImage', backgroundImage);
      if (backgroundVideo) formData.append('backgroundVideo', backgroundVideo);
    }

    const endpoint = editMode ? `/update-channel/${editingChannel._id}` : '/create-channel';
    const method   = editMode ? 'put' : 'post';

    api[method](endpoint, formData, {
      timeout: 600000,
      headers: {
        Authorization: `Bearer ${user.token}`,
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress({
          logo:  logo            ? percent : 0,
          image: backgroundImage ? percent : 0,
          video: backgroundVideo ? percent : 0,
        });
      },
    })
      .then(() => {
        setLoading(false);
        toast.success(editMode ? '✅ Channel updated!' : '✅ Channel created!');
        resetForm();
        setTimeout(() => {
          setCreateChannel(false);
          setChannelRefreshKey(Date.now());
        }, 800);
      })
      .catch((err) => {
        setLoading(false);
        console.error(err);
        toast.error('❌ Failed to submit channel.');
      });
  }, [channelName, niche, logo, backgroundImage, backgroundVideo, editMode, editingChannel, user.token, resetForm]);

  // ── Open create form ───────────────────────────────────────
  const handleCreateNew = useCallback(() => {
    resetForm();
    setCreateChannel(true);
  }, [resetForm]);

  // ── Open edit form (called from GetChannel card button) ────
  const handleEditChannel = useCallback((channel) => {
    setEditMode(true);
    setEditingChannel(channel);
    setChannelName(channel.channelName);
    setNiche(channel.niche);
    setCreateChannel(true);
  }, []);

  // ── Cancel form ────────────────────────────────────────────
  const handleCancel = useCallback(() => {
    resetForm();
    setCreateChannel(false);
  }, [resetForm]);

  // ── After delete: refresh grid ─────────────────────────────
  const handleDeleteChannel = useCallback(() => {
    setChannelRefreshKey(Date.now());
  }, []);

  return (
    <div className="channel">
      <div className="channel-head">
        <Header />
        <div className="create-channel-btn">
          <button onClick={handleCreateNew}>+ Create New Channel</button>
        </div>
      </div>

      {/* ── Create / Edit modal ──────────────────────────────── */}
      {createChannel && (
        <div className="create-channel" onClick={handleCancel}>
          <div className="form-container" onClick={e => e.stopPropagation()}>
            <form className="form" onSubmit={handleSubmit}>

              {editMode && editingChannel && (
                <div className="edit-hint">✏️ Editing: {editingChannel.channelName}</div>
              )}

              <div className="form-group">
                <label htmlFor="niche">Niche</label>
                <select id="niche" value={niche} onChange={e => setNiche(e.target.value)} required>
                  <option value="">Select a niche</option>
                  {NICHES.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="channel-name">Channel Name</label>
                <input
                  type="text"
                  id="channel-name"
                  value={channelName}
                  onChange={e => setChannelName(e.target.value)}
                  placeholder="Enter channel name…"
                  required
                />
              </div>

              <div className="form-group">
                <label>Upload Channel Logo {editMode && <span className="form-optional">(leave blank to keep current)</span>}</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={e => setLogo(e.target.files[0])}
                  {...(editMode ? {} : { required: true })}
                />
              </div>

              {niche === 'commentary' && (
                <>
                  <div className="form-group">
                    <label>Upload Avatar Image {editMode && <span className="form-optional">(optional)</span>}</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={e => setBackgroundImage(e.target.files[0])}
                      {...(editMode ? {} : { required: true })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Upload Background Video {editMode && <span className="form-optional">(optional)</span>}</label>
                    <input type="file" accept="video/*" onChange={e => setBackgroundVideo(e.target.files[0])} />
                  </div>
                </>
              )}

              <div className="channel-btns">
                <button className="form-submit-btn" type="submit" disabled={loading}>
                  {loading ? 'Submitting…' : editMode ? 'Update Channel' : 'Create Channel'}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className={editMode ? 'cancel-edit-btn' : ''}
                >
                  {editMode ? 'Cancel Edit' : 'Cancel'}
                </button>
              </div>
            </form>

            {/* ── Upload progress ─────────────────────────── */}
            {loading && (
              <div className="upload-data">
                <div className="uploading-panel">
                  <div className="uploading-title">Uploading your channel…</div>
                  {logo && (
                    <div className="upload-progress-bar">
                      <label>Logo</label>
                      <progress value={uploadProgress.logo} max="100">{uploadProgress.logo}%</progress>
                    </div>
                  )}
                  {niche === 'commentary' && backgroundImage && (
                    <div className="upload-progress-bar">
                      <label>Avatar Image</label>
                      <progress value={uploadProgress.image} max="100">{uploadProgress.image}%</progress>
                    </div>
                  )}
                  {niche === 'commentary' && backgroundVideo && (
                    <div className="upload-progress-bar">
                      <label>Background Video</label>
                      <progress value={uploadProgress.video} max="100">{uploadProgress.video}%</progress>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Channel grid ─────────────────────────────────────── */}
      <GetChannel
        refreshTrigger={channelRefreshKey}
        onEditChannel={handleEditChannel}
        onDeleteChannel={handleDeleteChannel}
      />
    </div>
  );
}

export default Channel;